import { DEFAULT_CHAIN_ID } from "@/constants";
import { AccountFactoryAddress } from "@/constants/addresses";
import {
  Address,
  BaseError,
  Client,
  decodeFunctionData,
  encodeFunctionData,
  Prettify,
  WalletClient,
} from "viem";
import {
  entryPoint06Abi,
  entryPoint06Address,
  SmartAccount,
  SmartAccountImplementation,
  toSmartAccount,
} from "viem/account-abstraction";
import { readContract } from "viem/actions";
import { getAction } from "viem/utils";

interface ToLogsSmartAccountParameters {
  address?: Address | undefined;
  client: Client;
  owner: WalletClient;
  nonce?: bigint | undefined;
  homeChain: bigint;
  homeCoin: Address;
  swapper: Address;
  bridger: Address;
  salt: bigint;
}

export type GetAccountNonceParams = {
  address: Address;
  entryPointAddress: Address;
  key?: bigint;
};

export const getAccountNonce = async (
  client: Client,
  args: GetAccountNonceParams
): Promise<bigint> => {
  const { address, entryPointAddress, key = BigInt(0) } = args;

  return await getAction(
    client,
    readContract,
    "readContract"
  )({
    address: entryPointAddress,
    abi: [
      {
        inputs: [
          {
            name: "sender",
            type: "address",
          },
          {
            name: "key",
            type: "uint192",
          },
        ],
        name: "getNonce",
        outputs: [
          {
            name: "nonce",
            type: "uint256",
          },
        ],
        stateMutability: "view",
        type: "function",
      },
    ],
    functionName: "getNonce",
    args: [address, key],
  });
};

export type ToLogsSmartAccountReturnType = Prettify<
  SmartAccount<LogsSmartAccountImplementation>
>;

export type LogsSmartAccountImplementation = SmartAccountImplementation<
  typeof entryPoint06Abi,
  "0.6",
  { abi: typeof abi; factory: { abi: typeof factoryAbi; address: Address } }
>;

export async function toLogsSmartAccount(
  parameters: ToLogsSmartAccountParameters
): Promise<ToLogsSmartAccountReturnType> {
  const {
    client,
    owner,
    nonce = 0n,
    homeChain,
    homeCoin,
    swapper,
    bridger,
    salt,
  } = parameters;

  let address = parameters.address;

  const myAccount = owner.account?.address!;

  const entryPoint = {
    abi: entryPoint06Abi,
    address: entryPoint06Address,
    version: "0.6",
  } as const;
  const factory = {
    abi: factoryAbi,
    address: AccountFactoryAddress[DEFAULT_CHAIN_ID]!,
  } as const;

  return toSmartAccount({
    client,
    entryPoint,
    extend: {
      abi,
      factory,
    },
    async getAddress() {
      if (address) return address;
      return await readContract(client, {
        ...factory,
        functionName: "getAddress",
        args: [homeChain, homeCoin, swapper, bridger, myAccount, salt],
      });
    },
    async getNonce(args) {
      return getAccountNonce(client, {
        address: await this.getAddress(),
        entryPointAddress: entryPoint.address,
        key: 0n,
      });
    },
    async encodeCalls(calls) {
      return encodeFunctionData({
        abi,
        functionName: "executeBatch",
        args: [
          calls.map((call) => ({
            data: call.data ?? "0x",
            dest: call.to, // 将 target 改为 dest 以匹配 ABI 定义
            value: call.value ?? 0n,
          })),
        ],
      });
    },

    async decodeCalls(data) {
      const result = decodeFunctionData({
        abi,
        data,
      });

      if (result.functionName === "executeBatch")
        return result.args[0].map((arg) => ({
          to: arg.dest,
          value: arg.value,
          data: arg.data,
        }));
      throw new BaseError(
        `unable to decode calls for "${result.functionName}"`
      );
    },
    async getFactoryArgs() {
      const factoryData = encodeFunctionData({
        abi: factory.abi,
        functionName: "createAccount",
        args: [homeChain, homeCoin, swapper, bridger, myAccount, salt, "0x"],
      });
      return {
        factory: factory.address,
        factoryData: factoryData,
      };
    },

    async getStubSignature() {
      return "0xfffffffffffffffffffffffffffffff0000000000000000000000000000000007aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa1c";
    },

    async signMessage(parameters) {
      // const { message } = parameters;
      // const [address, { factory, factoryData }] = await Promise.all([
      //   this.getAddress(),
      //   this.getFactoryArgs(),
      // ]);
      // return await signMessage(client, {
      //   account: owners[ownerIndex],
      //   factory,
      //   factoryData,
      //   message,
      //   verifier: address,
      // });
      return "0x";
    },

    async signTypedData(parameters) {
      // const { domain, types, primaryType, message } =
      //   parameters as TypedDataDefinition<TypedData, string>;
      // const [address, { factory, factoryData }] = await Promise.all([
      //   this.getAddress(),
      //   this.getFactoryArgs(),
      // ]);
      // return await signTypedData(client, {
      //   account: owner,
      //   domain,
      //   message,
      //   factory,
      //   factoryData,
      //   primaryType,
      //   types,
      //   verifier: address,
      // });
      return "0x";
    },
    async signUserOperation(parameters) {
      // const { chainId = client.chain!.id, ...userOperation } = parameters;

      // const address = await this.getAddress();
      // const userOpHash = getUserOperationHash({
      //   chainId,
      //   entryPointAddress: entryPoint.address,
      //   entryPointVersion: entryPoint.version,
      //   userOperation: {
      //     ...(userOperation as any),
      //     sender: address,
      //   },
      // });
      // const signature = await getAction(
      //   client,
      //   signMessage_,
      //   "signMessage"
      // )({
      //   account: owner,
      //   message: {
      //     raw: userOpHash,
      //   },
      // });
      return "0x";
    },
  });
}

const abi = [
  {
    type: "constructor",
    inputs: [
      {
        name: "_entryPoint",
        type: "address",
        internalType: "contract IEntryPoint",
      },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "receive",
    stateMutability: "payable",
  },
  {
    type: "function",
    name: "addSigningKey",
    inputs: [
      {
        name: "key",
        type: "address",
        internalType: "address",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "bridger",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract IBridger",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "collect",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "amountIn",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "tokenBridge",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "extraDataSwap",
        type: "bytes",
        internalType: "bytes",
      },
      {
        name: "extraDataBridge",
        type: "bytes",
        internalType: "bytes",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "entryPoint",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract IEntryPoint",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "executeBatch",
    inputs: [
      {
        name: "calls",
        type: "tuple[]",
        internalType: "struct LogsAccount.Call[]",
        components: [
          {
            name: "dest",
            type: "address",
            internalType: "address",
          },
          {
            name: "value",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "data",
            type: "bytes",
            internalType: "bytes",
          },
        ],
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "forward",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        internalType: "contract IERC20",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "forwardingAddress",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "address payable",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "getActiveSigningKeys",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address[]",
        internalType: "address[]",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "homeChain",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "homeCoin",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract IERC20",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "initialize",
    inputs: [
      {
        name: "_homeChain",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "_homeCoin",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "_swapper",
        type: "address",
        internalType: "contract ISwapper",
      },
      {
        name: "_bridger",
        type: "address",
        internalType: "contract IBridger",
      },
      {
        name: "key",
        type: "address",
        internalType: "address",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "keys",
    inputs: [
      {
        name: "",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "address",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "maxKeys",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "uint8",
        internalType: "uint8",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "numActiveKeys",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "uint8",
        internalType: "uint8",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "removeSigningKey",
    inputs: [
      {
        name: "currentKey",
        type: "address",
        internalType: "address",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "setForwardingAddress",
    inputs: [
      {
        name: "newAddr",
        type: "address",
        internalType: "address payable",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "swapToHomeCoin",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "amountIn",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "extraData",
        type: "bytes",
        internalType: "bytes",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "swapper",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract ISwapper",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "updateHomeCoin",
    inputs: [
      {
        name: "newHomeCoin",
        type: "address",
        internalType: "contract IERC20",
      },
    ],
    outputs: [],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "validateUserOp",
    inputs: [
      {
        name: "userOp",
        type: "tuple",
        internalType: "struct UserOperation",
        components: [
          {
            name: "sender",
            type: "address",
            internalType: "address",
          },
          {
            name: "nonce",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "initCode",
            type: "bytes",
            internalType: "bytes",
          },
          {
            name: "callData",
            type: "bytes",
            internalType: "bytes",
          },
          {
            name: "callGasLimit",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "verificationGasLimit",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "preVerificationGas",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "maxFeePerGas",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "maxPriorityFeePerGas",
            type: "uint256",
            internalType: "uint256",
          },
          {
            name: "paymasterAndData",
            type: "bytes",
            internalType: "bytes",
          },
          {
            name: "signature",
            type: "bytes",
            internalType: "bytes",
          },
        ],
      },
      {
        name: "userOpHash",
        type: "bytes32",
        internalType: "bytes32",
      },
      {
        name: "missingAccountFunds",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    outputs: [
      {
        name: "validationData",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "event",
    name: "AccountInitialized",
    inputs: [
      {
        name: "entryPoint",
        type: "address",
        indexed: true,
        internalType: "contract IEntryPoint",
      },
      {
        name: "homeChain",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
      {
        name: "homeCoin",
        type: "address",
        indexed: false,
        internalType: "contract IERC20",
      },
      {
        name: "swapper",
        type: "address",
        indexed: false,
        internalType: "contract ISwapper",
      },
      {
        name: "bridger",
        type: "address",
        indexed: false,
        internalType: "contract IBridger",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "AutoSwap",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "amountIn",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
      {
        name: "tokenOut",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "amountOut",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "Collect",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "amountIn",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
      {
        name: "tokenBridge",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "amountBridge",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
      {
        name: "toChainID",
        type: "uint256",
        indexed: true,
        internalType: "uint256",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "ForwardAsset",
    inputs: [
      {
        name: "tokenIn",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "amountIn",
        type: "uint256",
        indexed: true,
        internalType: "uint256",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "Initialized",
    inputs: [
      {
        name: "version",
        type: "uint64",
        indexed: false,
        internalType: "uint64",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "SetForwardingAddress",
    inputs: [
      {
        name: "forwardingAddress",
        type: "address",
        indexed: false,
        internalType: "address",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "SigningKeyAdded",
    inputs: [
      {
        name: "key",
        type: "address",
        indexed: false,
        internalType: "address",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "SigningKeyRemoved",
    inputs: [
      {
        name: "key",
        type: "address",
        indexed: false,
        internalType: "address",
      },
    ],
    anonymous: false,
  },
  {
    type: "event",
    name: "UpdateHomeCoin",
    inputs: [
      {
        name: "oldHomeCoin",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
      {
        name: "newHomeCoin",
        type: "address",
        indexed: true,
        internalType: "contract IERC20",
      },
    ],
    anonymous: false,
  },
  {
    type: "error",
    name: "ECDSAInvalidSignature",
    inputs: [],
  },
  {
    type: "error",
    name: "ECDSAInvalidSignatureLength",
    inputs: [
      {
        name: "length",
        type: "uint256",
        internalType: "uint256",
      },
    ],
  },
  {
    type: "error",
    name: "ECDSAInvalidSignatureS",
    inputs: [
      {
        name: "s",
        type: "bytes32",
        internalType: "bytes32",
      },
    ],
  },
  {
    type: "error",
    name: "InvalidInitialization",
    inputs: [],
  },
  {
    type: "error",
    name: "NotInitializing",
    inputs: [],
  },
  {
    type: "error",
    name: "ReentrancyGuardReentrantCall",
    inputs: [],
  },
  {
    type: "error",
    name: "SafeERC20FailedOperation",
    inputs: [
      {
        name: "token",
        type: "address",
        internalType: "address",
      },
    ],
  },
] as const;

const factoryAbi = [
  {
    type: "constructor",
    inputs: [
      {
        name: "_entryPoint",
        type: "address",
        internalType: "contract IEntryPoint",
      },
    ],
    stateMutability: "nonpayable",
  },
  {
    type: "function",
    name: "accountImplementation",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract LogsAccount",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "createAccount",
    inputs: [
      {
        name: "homeChain",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "homeCoin",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "swapper",
        type: "address",
        internalType: "contract ISwapper",
      },
      {
        name: "bridger",
        type: "address",
        internalType: "contract IBridger",
      },
      {
        name: "key",
        type: "address",
        internalType: "address",
      },
      {
        name: "salt",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "signature",
        type: "bytes",
        internalType: "bytes",
      },
    ],
    outputs: [
      {
        name: "ret",
        type: "address",
        internalType: "contract LogsAccount",
      },
    ],
    stateMutability: "payable",
  },
  {
    type: "function",
    name: "entryPoint",
    inputs: [],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "contract IEntryPoint",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "getAddress",
    inputs: [
      {
        name: "homeChain",
        type: "uint256",
        internalType: "uint256",
      },
      {
        name: "homeCoin",
        type: "address",
        internalType: "contract IERC20",
      },
      {
        name: "swapper",
        type: "address",
        internalType: "contract ISwapper",
      },
      {
        name: "bridger",
        type: "address",
        internalType: "contract IBridger",
      },
      {
        name: "key",
        type: "address",
        internalType: "address",
      },
      {
        name: "salt",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "address",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "function",
    name: "subAccounts",
    inputs: [
      {
        name: "",
        type: "address",
        internalType: "address",
      },
      {
        name: "",
        type: "uint256",
        internalType: "uint256",
      },
    ],
    outputs: [
      {
        name: "",
        type: "address",
        internalType: "address",
      },
    ],
    stateMutability: "view",
  },
  {
    type: "event",
    name: "CreateAccount",
    inputs: [
      {
        name: "addr",
        type: "address",
        indexed: false,
        internalType: "address",
      },
      {
        name: "homeChain",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
      {
        name: "homeCoin",
        type: "address",
        indexed: false,
        internalType: "address",
      },
      {
        name: "swapper",
        type: "address",
        indexed: false,
        internalType: "address",
      },
      {
        name: "bridger",
        type: "address",
        indexed: false,
        internalType: "address",
      },
      {
        name: "key",
        type: "address",
        indexed: false,
        internalType: "address",
      },
      {
        name: "salt",
        type: "uint256",
        indexed: false,
        internalType: "uint256",
      },
    ],
    anonymous: false,
  },
  {
    type: "error",
    name: "ECDSAInvalidSignature",
    inputs: [],
  },
  {
    type: "error",
    name: "ECDSAInvalidSignatureLength",
    inputs: [
      {
        name: "length",
        type: "uint256",
        internalType: "uint256",
      },
    ],
  },
  {
    type: "error",
    name: "ECDSAInvalidSignatureS",
    inputs: [
      {
        name: "s",
        type: "bytes32",
        internalType: "bytes32",
      },
    ],
  },
] as const;
